/** Read-through cache for the surfaces the day view needs.
 *
 *  Cached views paint first and revalidate behind, so opening the app on a slow
 *  or absent connection still shows the journal. Keys are namespaced by
 *  destination so two servers cannot show each other's days.
 */

import { CACHE_STORE, idb } from './db';
import type { Destination } from './destinations';

interface Entry<T> {
  value: T;
  storedAt: string;
}

function key(destination: Destination, path: string): string {
  return `${destination.id}:${path}`;
}

export async function remember<T>(
  destination: Destination,
  path: string,
  value: T,
): Promise<void> {
  const entry: Entry<T> = { value, storedAt: new Date().toISOString() };
  await idb.put(CACHE_STORE, entry, key(destination, path));
}

export async function recall<T>(
  destination: Destination,
  path: string,
): Promise<{ value: T; storedAt: string } | null> {
  const entry = await idb.get<Entry<T>>(CACHE_STORE, key(destination, path));
  return entry ? { value: entry.value, storedAt: entry.storedAt } : null;
}

/** How stale a cached view is, in words, for the UI to state plainly. */
export function ageInWords(storedAt: string, now = new Date()): string {
  const seconds = Math.max(0, Math.round((now.getTime() - new Date(storedAt).getTime()) / 1000));
  if (seconds < 60) return 'JUST NOW';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}M OLD`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}H OLD`;
  return `${Math.round(hours / 24)}D OLD`;
}
